import React, { useState } from 'react';
import { toast } from 'sonner';
import { useCreateCareTeam } from '../../lib/hooks/useCareTeams';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Users, Check, ArrowLeft, ArrowRight, UserCog, Activity, Heart } from 'lucide-react';

interface Doctor {
  id: number;
  name: string;
  specialization?: string;
  hospital?: string;  // String hospital name from backend
  hospital_name?: string;  // Alternative field name
  hospital_id?: number;  // Legacy/deprecated
  hospitalId?: number;  // Legacy/deprecated
  availability?: string;
}

interface Therapist {
  id: number;
  name: string;
  specialization?: string;
  hospital_id?: number;
  hospital_name?: string;  // Added to match backend response
  hospitalId?: number;
  availability?: string;
}

interface Nurse {
  id: number;
  name: string;
  specialization?: string;
  hospital_id?: number;
  hospital_name?: string;  // Added to match backend response
  hospitalId?: number;
  availability?: string;
}

interface PhysicianAssistant {
  id: number;
  name: string;
  specialization?: string;
  hospital_id?: number;
  hospital_name?: string;
  hospitalId?: number;
  availability?: string;
}

interface CreateCareTeamModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hospitals: Array<{ id: number; name: string }>;
  doctors?: Doctor[];
  therapists?: Therapist[];
  nurses?: Nurse[];
  physicianAssistants?: PhysicianAssistant[];
}

export function CreateCareTeamModal({ open, onOpenChange, hospitals, doctors = [], therapists = [], nurses = [], physicianAssistants = [] }: CreateCareTeamModalProps) {
  const [step, setStep] = useState(1);
  const [selectedHospital, setSelectedHospital] = useState<string>('');
  const [selectedHospitalName, setSelectedHospitalName] = useState('');
  const [selectedDoctors, setSelectedDoctors] = useState<number[]>([]);
  const [selectedTherapists, setSelectedTherapists] = useState<number[]>([]);
  const [selectedNurses, setSelectedNurses] = useState<number[]>([]);
  const [selectedPhysicianAssistants, setSelectedPhysicianAssistants] = useState<number[]>([]);
  const [teamName, setTeamName] = useState('');
  const [teamDescription, setTeamDescription] = useState('');

  // ✅ FIX 1: Call hook at top level (not inside event handler)
  const createCareTeamMutation = useCreateCareTeam();

  // Filter doctors by selected hospital
  const hospitalDoctors = doctors.filter(doc => {
    if (!selectedHospitalName) return false;
    const docHospital = doc.hospital_name || doc.hospital || (doc as any).hospitalName;
    // Case-insensitive comparison with trimming
    return docHospital && docHospital.trim().toLowerCase() === selectedHospitalName.trim().toLowerCase();
  });

  // Filter therapists by selected hospital
  const hospitalTherapists = therapists.filter(therapist => {
    if (!selectedHospitalName) return false;
    const therapistHospitalName = therapist.hospital_name || (therapist as any).hospitalName;
    // Case-insensitive comparison with trimming
    return therapistHospitalName && therapistHospitalName.trim().toLowerCase() === selectedHospitalName.trim().toLowerCase();
  });

  // Filter nurses by selected hospital
  const hospitalNurses = nurses.filter(nurse => {
    if (!selectedHospitalName) return false;
    const nurseHospitalName = nurse.hospital_name || (nurse as any).hospitalName;
    // Case-insensitive comparison with trimming
    return nurseHospitalName && nurseHospitalName.trim().toLowerCase() === selectedHospitalName.trim().toLowerCase();
  });

  // Filter physician assistants by selected hospital
  const hospitalPhysicianAssistants = physicianAssistants.filter(pa => {
    if (!selectedHospitalName) return false;
    const paHospitalName = pa.hospital_name || (pa as any).hospitalName;
    // Case-insensitive comparison with trimming
    return paHospitalName && paHospitalName.trim().toLowerCase() === selectedHospitalName.trim().toLowerCase();
  });

  const handleHospitalChange = (hospitalId: string) => {
    setSelectedHospital(hospitalId);
    const hospital = hospitals.find(h => h.id.toString() === hospitalId);
    setSelectedHospitalName(hospital?.name || '');
    setSelectedDoctors([]);
    setSelectedTherapists([]);
    setSelectedNurses([]);
    setSelectedPhysicianAssistants([]);
    
    // Debug logging
    console.log('Selected hospital:', hospital?.name);
    console.log('Total doctors:', doctors.length);
    console.log('Total therapists:', therapists.length);
    console.log('Total nurses:', nurses.length);
    console.log('Total physician assistants:', physicianAssistants.length);
    console.log('Doctors data sample:', doctors.slice(0, 2));
    console.log('Therapists data sample:', therapists.slice(0, 2));
    console.log('Nurses data sample:', nurses.slice(0, 2));
    console.log('Physician assistants data sample:', physicianAssistants.slice(0, 2));
  };

  const toggleDoctor = (doctorId: number) => {
    setSelectedDoctors(prev =>
      prev.includes(doctorId)
        ? prev.filter(id => id !== doctorId)
        : [...prev, doctorId]
    );
  };

  const toggleTherapist = (therapistId: number) => {
    // ✅ FIX: Only allow ONE therapist to be selected (radio button behavior)
    setSelectedTherapists(prev =>
      prev.includes(therapistId)
        ? [] // Deselect if already selected
        : [therapistId] // Replace with new selection
    );
  };

  const toggleNurse = (nurseId: number) => {
    // ✅ FIX: Only allow ONE nurse to be selected (radio button behavior)
    setSelectedNurses(prev =>
      prev.includes(nurseId)
        ? [] // Deselect if already selected
        : [nurseId] // Replace with new selection
    );
  };

  const togglePhysicianAssistant = (paId: number) => {
    // ✅ FIX: Only allow ONE physician assistant to be selected (radio button behavior)
    setSelectedPhysicianAssistants(prev =>
      prev.includes(paId)
        ? [] // Deselect if already selected
        : [paId] // Replace with new selection
    );
  };

  const handleNext = () => {
    if (step === 1 && !selectedHospital) {
      toast.error('Please select a hospital');
      return;
    }
    if (step === 2 && selectedDoctors.length === 0 && selectedTherapists.length === 0 && selectedNurses.length === 0 && selectedPhysicianAssistants.length === 0) {
      toast.error('Please select at least one doctor, therapist, nurse, or physician assistant');
      return;
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleCreate = () => {
    if (!teamName.trim()) {
      toast.error('Please enter a team name');
      return;
    }
    if (!teamDescription.trim()) {
      toast.error('Please enter a team description');
      return;
    }

    // ✅ FIX: Merge PA IDs with doctor IDs since PAs are now in doctors table
    const allDoctorIds = [...selectedDoctors, ...selectedPhysicianAssistants];
    
    // ✅ FIX: Send singular therapist_id and nurse_id (first element of array or undefined)
    createCareTeamMutation.mutate({
      name: teamName,
      description: teamDescription,
      hospital_id: parseInt(selectedHospital),
      doctor_ids: allDoctorIds, // ✅ FIXED: Merged doctors and PAs into single array
      // ✅ REMOVED: physician_assistant_ids (backend no longer accepts this)
      therapist_id: selectedTherapists.length > 0 ? selectedTherapists[0] : undefined,
      nurse_id: selectedNurses.length > 0 ? selectedNurses[0] : undefined,
    }, {
      onSuccess: () => {
        // Reset form on success
        setStep(1);
        setSelectedHospital('');
        setSelectedHospitalName('');
        setSelectedDoctors([]);
        setSelectedTherapists([]);
        setSelectedNurses([]);
        setSelectedPhysicianAssistants([]);
        setTeamName('');
        setTeamDescription('');
        onOpenChange(false);
      },
      onError: (error: any) => {
        // Error handling is done by the mutation hook
        console.error('Failed to create care team:', error);
      }
    });
  };

  const getDoctorById = (id: number): Doctor | undefined => {
    return doctors.find(d => d.id === id);
  };

  const getTherapistById = (id: number): Therapist | undefined => {
    return therapists.find(t => t.id === id);
  };

  const getNurseById = (id: number): Nurse | undefined => {
    return nurses.find(n => n.id === id);
  };

  const getPhysicianAssistantById = (id: number): PhysicianAssistant | undefined => {
    return physicianAssistants.find(pa => pa.id === id);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-primary text-xl flex items-center gap-2">
            <Users className="w-6 h-6" />
            Create New Care Team
          </DialogTitle>
          <DialogDescription>
            Follow the steps to create a new care team
          </DialogDescription>
        </DialogHeader>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center mb-6 mt-4">
          <div className="flex items-center gap-2">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'}`}>
              {step > 1 ? <Check className="w-5 h-5" /> : '1'}
            </div>
            <div className={`w-20 h-1 ${step >= 2 ? 'bg-primary' : 'bg-secondary'}`} />
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'}`}>
              {step > 2 ? <Check className="w-5 h-5" /> : '2'}
            </div>
            <div className={`w-20 h-1 ${step >= 3 ? 'bg-primary' : 'bg-secondary'}`} />
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'}`}>
              {step > 3 ? <Check className="w-5 h-5" /> : '3'}
            </div>
          </div>
        </div>

        {/* Step 1: Select Hospital */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="bg-[#D4F0F8] p-4 rounded-xl">
              <h3 className="text-primary mb-1">Step 1: Select Hospital</h3>
              <p className="text-muted-foreground text-sm">Choose the hospital for this care team</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="hospital">Hospital *</Label>
              <Select value={selectedHospital} onValueChange={handleHospitalChange}>
                <SelectTrigger id="hospital" className="bg-[#D4F0F8]">
                  <SelectValue placeholder="Select a hospital" />
                </SelectTrigger>
                <SelectContent>
                  {hospitals.map(hospital => (
                    <SelectItem key={hospital.id} value={hospital.id.toString()}>
                      {hospital.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedHospital && (
              <div className="p-4 bg-secondary rounded-lg space-y-2">
                <p className="text-sm text-muted-foreground">
                  Selected Hospital: <span className="text-primary">{selectedHospitalName}</span>
                </p>
                <p className="text-sm text-muted-foreground">
                  Available Doctors: <span className="text-primary">{hospitalDoctors.length}</span>
                </p>
                <p className="text-sm text-muted-foreground">
                  Available Therapists: <span className="text-primary">{hospitalTherapists.length}</span>
                </p>
                <p className="text-sm text-muted-foreground">
                  Available Nurses: <span className="text-primary">{hospitalNurses.length}</span>
                </p>
                <p className="text-sm text-muted-foreground">
                  Available Physician Assistants: <span className="text-primary">{hospitalPhysicianAssistants.length}</span>
                </p>
                
                {/* Debug info */}
                <details className="mt-2">
                  <summary className="text-xs text-muted-foreground cursor-pointer hover:text-primary">
                    Debug: View filtered data
                  </summary>
                  <div className="mt-2 p-2 bg-background rounded text-xs space-y-1">
                    <p>Total doctors in system: {doctors.length}</p>
                    <p>Total therapists in system: {therapists.length}</p>
                    <p>Total nurses in system: {nurses.length}</p>
                    <p>Total physician assistants in system: {physicianAssistants.length}</p>
                    {doctors.length > 0 && (
                      <div>
                        <p className="font-medium mt-2">Sample doctor hospital values:</p>
                        {doctors.slice(0, 3).map((d, i) => (
                          <p key={i}>- "{d.hospital_name || d.hospital || 'none'}"</p>
                        ))}
                      </div>
                    )}
                  </div>
                </details>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Select Team Members (Doctors, Therapists, Nurses) */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="bg-[#D4F0F8] p-4 rounded-xl">
              <h3 className="text-primary mb-1">Step 2: Select Team Members</h3>
              <p className="text-muted-foreground text-sm">Choose doctors (multiple), one therapist, one nurse, and one physician assistant for this care team</p>
            </div>

            <div className="space-y-6">
              {/* Doctors Section */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <UserCog className="w-5 h-5 text-[#2762AC]" />
                  <h4 className="text-primary">Doctors ({hospitalDoctors.length} available)</h4>
                </div>
                
                <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                  {hospitalDoctors.length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground text-sm bg-secondary rounded-lg">
                      No doctors available for this hospital
                    </div>
                  ) : (
                    hospitalDoctors.map(doctor => (
                      <div
                        key={doctor.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-all ${
                          selectedDoctors.includes(doctor.id)
                            ? 'border-[#2762AC] bg-[#2762AC]/5'
                            : 'border-border hover:border-[#2762AC]/50'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={selectedDoctors.includes(doctor.id)}
                            onCheckedChange={() => toggleDoctor(doctor.id)}
                          />
                          <div className="flex-1">
                            <p className="font-medium">{doctor.name}</p>
                            {doctor.specialization && (
                              <p className="text-sm text-muted-foreground">{doctor.specialization}</p>
                            )}
                            {doctor.availability && (
                              <Badge
                                className={
                                  doctor.availability === 'Available'
                                    ? 'bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4] mt-1'
                                    : 'bg-[#494949]/10 text-[#494949] border-[#494949] mt-1'
                                }
                                variant="outline"
                              >
                                {doctor.availability}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Therapists Section */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-[#F07D13]" />
                  <h4 className="text-primary">Therapists ({hospitalTherapists.length} available)</h4>
                </div>
                <p className="text-xs text-muted-foreground italic">Select one therapist (optional)</p>
                
                <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                  {hospitalTherapists.length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground text-sm bg-secondary rounded-lg">
                      No therapists available for this hospital
                    </div>
                  ) : (
                    hospitalTherapists.map(therapist => (
                      <div
                        key={therapist.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-all ${
                          selectedTherapists.includes(therapist.id)
                            ? 'border-[#F07D13] bg-[#F07D13]/5'
                            : 'border-border hover:border-[#F07D13]/50'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={selectedTherapists.includes(therapist.id)}
                            onCheckedChange={() => toggleTherapist(therapist.id)}
                          />
                          <div className="flex-1">
                            <p className="font-medium">{therapist.name}</p>
                            {therapist.specialization && (
                              <p className="text-sm text-muted-foreground">{therapist.specialization}</p>
                            )}
                            {therapist.availability && (
                              <Badge
                                className={
                                  therapist.availability === 'Available'
                                    ? 'bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4] mt-1'
                                    : 'bg-[#494949]/10 text-[#494949] border-[#494949] mt-1'
                                }
                                variant="outline"
                              >
                                {therapist.availability}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Nurses Section */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Heart className="w-5 h-5 text-[#14CAF4]" />
                  <h4 className="text-primary">Nurses ({hospitalNurses.length} available)</h4>
                </div>
                <p className="text-xs text-muted-foreground italic">Select one nurse (optional)</p>
                
                <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                  {hospitalNurses.length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground text-sm bg-secondary rounded-lg">
                      No nurses available for this hospital
                    </div>
                  ) : (
                    hospitalNurses.map(nurse => (
                      <div
                        key={nurse.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-all ${
                          selectedNurses.includes(nurse.id)
                            ? 'border-[#14CAF4] bg-[#14CAF4]/5'
                            : 'border-border hover:border-[#14CAF4]/50'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={selectedNurses.includes(nurse.id)}
                            onCheckedChange={() => toggleNurse(nurse.id)}
                          />
                          <div className="flex-1">
                            <p className="font-medium">{nurse.name}</p>
                            {nurse.specialization && (
                              <p className="text-sm text-muted-foreground">{nurse.specialization}</p>
                            )}
                            {nurse.availability && (
                              <Badge
                                className={
                                  nurse.availability === 'Available'
                                    ? 'bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4] mt-1'
                                    : 'bg-[#494949]/10 text-[#494949] border-[#494949] mt-1'
                                }
                                variant="outline"
                              >
                                {nurse.availability}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Physician Assistants Section */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Heart className="w-5 h-5 text-[#14CAF4]" />
                  <h4 className="text-primary">Physician Assistants ({hospitalPhysicianAssistants.length} available)</h4>
                </div>
                <p className="text-xs text-muted-foreground italic">Select one physician assistant (optional)</p>
                
                <div className="space-y-3 max-h-60 overflow-y-auto pr-2">
                  {hospitalPhysicianAssistants.length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground text-sm bg-secondary rounded-lg">
                      No physician assistants available for this hospital
                    </div>
                  ) : (
                    hospitalPhysicianAssistants.map(pa => (
                      <div
                        key={pa.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-all ${
                          selectedPhysicianAssistants.includes(pa.id)
                            ? 'border-[#14CAF4] bg-[#14CAF4]/5'
                            : 'border-border hover:border-[#14CAF4]/50'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={selectedPhysicianAssistants.includes(pa.id)}
                            onCheckedChange={() => togglePhysicianAssistant(pa.id)}
                          />
                          <div className="flex-1">
                            <p className="font-medium">{pa.name}</p>
                            {pa.specialization && (
                              <p className="text-sm text-muted-foreground">{pa.specialization}</p>
                            )}
                            {pa.availability && (
                              <Badge
                                className={
                                  pa.availability === 'Available'
                                    ? 'bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4] mt-1'
                                    : 'bg-[#494949]/10 text-[#494949] border-[#494949] mt-1'
                                }
                                variant="outline"
                              >
                                {pa.availability}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Selected Members Summary */}
            {(selectedDoctors.length > 0 || selectedTherapists.length > 0 || selectedNurses.length > 0 || selectedPhysicianAssistants.length > 0) && (
              <div className="p-4 bg-secondary rounded-lg space-y-3">
                <h4 className="text-sm text-primary">Selected Team Members</h4>
                
                {selectedDoctors.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Doctors: {selectedDoctors.length}</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedDoctors.map(doctorId => {
                        const doctor = getDoctorById(doctorId);
                        return doctor ? (
                          <Badge key={doctorId} className="bg-[#2762AC] text-white">
                            {doctor.name}
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}
                
                {selectedTherapists.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Therapists: {selectedTherapists.length}</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedTherapists.map(therapistId => {
                        const therapist = getTherapistById(therapistId);
                        return therapist ? (
                          <Badge key={therapistId} className="bg-[#F07D13] text-white">
                            {therapist.name}
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}
                
                {selectedNurses.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Nurses: {selectedNurses.length}</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedNurses.map(nurseId => {
                        const nurse = getNurseById(nurseId);
                        return nurse ? (
                          <Badge key={nurseId} className="bg-[#14CAF4] text-white">
                            {nurse.name}
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}
                
                {selectedPhysicianAssistants.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Physician Assistants: {selectedPhysicianAssistants.length}</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedPhysicianAssistants.map(paId => {
                        const pa = getPhysicianAssistantById(paId);
                        return pa ? (
                          <Badge key={paId} className="bg-[#14CAF4] text-white">
                            {pa.name}
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 3: Team Name and Description */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="bg-[#D4F0F8] p-4 rounded-xl">
              <h3 className="text-primary mb-1">Step 3: Team Details</h3>
              <p className="text-muted-foreground text-sm">Provide a name and description for the care team</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="teamName">Team Name *</Label>
              <Input
                id="teamName"
                placeholder="e.g., Apollo Pulmonary Care Team A"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                className="bg-[#D4F0F8]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="teamDescription">Team Description *</Label>
              <Textarea
                id="teamDescription"
                placeholder="e.g., Primary pulmonary care team for COPD and asthma patients"
                value={teamDescription}
                onChange={(e) => setTeamDescription(e.target.value)}
                className="bg-[#D4F0F8] min-h-[100px]"
              />
            </div>

            {/* Summary */}
            <div className="p-4 bg-secondary rounded-lg space-y-3">
              <h4 className="text-sm text-primary">Care Team Summary</h4>
              <div className="space-y-2 text-sm">
                <p className="text-muted-foreground">
                  Hospital: <span className="text-foreground">{selectedHospitalName}</span>
                </p>
                <p className="text-muted-foreground">
                  Total Members: <span className="text-foreground">{selectedDoctors.length + selectedTherapists.length + selectedNurses.length + selectedPhysicianAssistants.length}</span>
                </p>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  <div className="text-center p-2 bg-[#2762AC]/10 rounded">
                    <p className="text-xs text-muted-foreground">Doctors</p>
                    <p className="text-lg text-[#2762AC]">{selectedDoctors.length}</p>
                  </div>
                  <div className="text-center p-2 bg-[#F07D13]/10 rounded">
                    <p className="text-xs text-muted-foreground">Therapists</p>
                    <p className="text-lg text-[#F07D13]">{selectedTherapists.length}</p>
                  </div>
                  <div className="text-center p-2 bg-[#14CAF4]/10 rounded">
                    <p className="text-xs text-muted-foreground">Nurses</p>
                    <p className="text-lg text-[#14CAF4]">{selectedNurses.length}</p>
                  </div>
                  <div className="text-center p-2 bg-[#14CAF4]/10 rounded">
                    <p className="text-xs text-muted-foreground">Physician Assistants</p>
                    <p className="text-lg text-[#14CAF4]">{selectedPhysicianAssistants.length}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={step === 1 ? () => onOpenChange(false) : handleBack}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {step === 1 ? 'Cancel' : 'Back'}
          </Button>

          {step < 3 ? (
            <Button onClick={handleNext} className="bg-[#14CAF4] hover:bg-[#14CAF4]/90">
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleCreate} className="bg-[#F07D13] hover:bg-[#F07D13]/90">
              <Check className="w-4 h-4 mr-2" />
              Create Team
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}